// "Commandes confirmées" — admin dashboard widget (2026-07-24).
//
// Scope is deliberately narrow (Yahya): HOW MANY orders were confirmed in the
// period, WHICH ones, and WHO confirmed each. No ad-spend, no ROI, no
// cost-per-order — that math is done manually elsewhere.
//
// ONE thing counts as "confirmed" (2026-07-28): an agent clicked
// "✓ Confirmer la commande". Reaching ready-to-print — by dropdown, bulk
// update, or an automated transition — does NOT count. See the header of
// GET /orders/confirmations for why the old two-source version was wrong.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ordersApi } from '@/lib/api';
import { useT } from '@/lib/i18n';

const GOLD = '#C9A24A';

export function ConfirmedOrdersCard({ range }: { range?: { from?: string; to?: string } }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['confirmations', range?.from ?? null, range?.to ?? null],
    queryFn: () => ordersApi.confirmations({ from: range?.from, to: range?.to }),
    staleTime: 60_000,
  });

  const rows = data?.confirmations ?? [];
  const count = data?.count ?? 0;
  const byAgent = new Map<string, number>();
  for (const r of rows) {
    const k = r.agent_name ?? t('Agent inconnu');
    byAgent.set(k, (byAgent.get(k) ?? 0) + 1);
  }

  return (
    <div
      className="mb-7 bg-white rounded-xl border overflow-hidden animate-[fadeInUp_0.5s_ease-out_both]"
      style={{ borderColor: 'rgba(201, 162, 74, 0.15)', boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04)' }}
    >
      {/* Headline: the count is the button that opens the list. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={open}
      >
        <span
          className="w-11 h-11 rounded-xl flex items-center justify-center text-lg shrink-0 bg-[#DCFCE7] dark:bg-status-done-bg"
          style={{ color: 'var(--color-status-done)' }}
          aria-hidden
        >
          ✓
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-2xl font-bold tabular-nums" style={{ color: 'var(--color-adm-ink)' }}>
            {isLoading ? '…' : count}
          </span>
          <span className="block text-xs text-gray-500">
            {t('Commandes confirmées sur la période')}
          </span>
        </span>
        <span className="text-xs font-semibold shrink-0" style={{ color: GOLD }}>
          {open ? t('Masquer') : t('Voir la liste')}
        </span>
      </button>

      {/* Per-agent tally — a one-line read of who confirmed what. */}
      {!isLoading && count > 0 && (
        <div className="px-5 pb-3 flex flex-wrap gap-2">
          {[...byAgent.entries()].sort((a, b) => b[1] - a[1]).map(([name, n]) => (
            <span
              key={name}
              className="px-2.5 py-1 rounded-full text-[11px] font-medium text-[#6B5423] dark:text-adm-gold-ink"
              style={{ background: 'rgba(201,162,74,0.10)' }}
            >
              {name} · {n}
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="border-t" style={{ borderColor: 'rgba(201, 162, 74, 0.15)' }}>
          {error && (
            <p className="px-5 py-4 text-sm text-red-600">{(error as Error).message}</p>
          )}
          {!error && rows.length === 0 && (
            <p className="px-5 py-4 text-sm text-gray-500">
              {t('Aucune commande confirmée sur cette période.')}
            </p>
          )}
          {rows.length > 0 && (
            <div className="max-h-[420px] overflow-y-auto">
              {rows.map((r) => (
                <div
                  key={r.order_id}
                  className="px-5 py-3 border-b last:border-b-0 flex items-center gap-3"
                  style={{ borderColor: 'rgba(201, 162, 74, 0.10)' }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate" style={{ color: 'var(--color-adm-ink)' }}>
                      {r.order_name ?? r.shopify_order_id ?? '—'}
                      {r.bundle && (
                        <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded"
                          style={{ background: 'var(--color-adm-surface-3)', color: 'var(--color-adm-muted)' }}>
                          {r.bundle}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{r.customer_name ?? '—'}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs font-medium" style={{ color: r.agent_name ? 'var(--color-adm-ink-2)' : '#9CA3AF' }}>
                      {r.agent_name ?? t('Agent inconnu')}
                    </div>
                    <div className="text-[11px] text-gray-400 tabular-nums">
                      {new Date(r.confirmed_at).toLocaleDateString('fr-FR', {
                        day: '2-digit', month: '2-digit', year: '2-digit',
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
